// useWindowManager.js
//
// Build 28 (item 1): content-agnostic floating-window state.
//
// The desktop can have many windows open at once (notes, diagrams,
// defects, features) for side-by-side comparison. This hook owns the
// list and the operations; the WINDOW CHROME is a separate component
// (FloatingWindow) and the WINDOW CONTENTS are the existing viewers
// (NoteViewer, EntityViewer, DiagramViewer) — none of which this hook
// knows or cares about. It just tracks geometry + z-order + a
// `kind`/`payload` so Desktop can decide what to render inside.
//
// One hook, reused for every content type → satisfies the DRY/custom-
// hook guidance instead of duplicating open/close/focus logic per type.

import { useState, useCallback, useRef } from 'react';

let _seq = 1;
const nextId = () => `win_${_seq++}`;

const SPAWN_BASE = { x: 80, y: 70 };
const SPAWN_STEP = 28;
const DEFAULT_SIZE = { w: 720, h: 560 };

export function useWindowManager() {
  const [windows, setWindows] = useState([]);
  const topZ = useRef(10);
  const spawnCount = useRef(0);

  const focus = useCallback((id) => {
    // v1.0.4: focus changes z-order ONLY. Previously it also set
    // minimized:false, which created a conflict: clicking the minimize
    // button caused mousedown to bubble (focus → un-minimize) just
    // before click (minimize → minimize), and depending on event order
    // the window stayed visible. Separating concerns means minimize
    // can never be undone by a focus event.
    setWindows(prev => {
      const z = ++topZ.current;
      return prev.map(w => (w.id === id ? { ...w, z } : w));
    });
  }, []);

  // Open a window. `dedupeKey` lets callers avoid duplicate windows for
  // the same underlying item — if a window with that key exists we just
  // focus it instead of opening a second copy.
  const open = useCallback(({ kind, payload, title, dedupeKey, size }) => {
    let focusedExistingId = null;
    setWindows(prev => {
      if (dedupeKey) {
        const existing = prev.find(w => w.dedupeKey === dedupeKey);
        if (existing) {
          focusedExistingId = existing.id;
          const z = ++topZ.current;
          return prev.map(w =>
            w.id === existing.id ? { ...w, z, minimized: false } : w
          );
        }
      }
      const n = spawnCount.current++;
      const z = ++topZ.current;
      const win = {
        id: nextId(),
        kind,
        payload,
        title: title || 'Window',
        dedupeKey: dedupeKey || null,
        x: SPAWN_BASE.x + (n % 6) * SPAWN_STEP,
        y: SPAWN_BASE.y + (n % 6) * SPAWN_STEP,
        w: (size && size.w) || DEFAULT_SIZE.w,
        h: (size && size.h) || DEFAULT_SIZE.h,
        z,
        minimized: false
      };
      return [...prev, win];
    });
    return focusedExistingId; // null if a new window was created
  }, []);

  const close = useCallback((id) => {
    setWindows(prev => prev.filter(w => w.id !== id));
  }, []);

  const closeAll = useCallback(() => setWindows([]), []);

  const minimize = useCallback((id) => {
    setWindows(prev => prev.map(w => (w.id === id ? { ...w, minimized: true } : w)));
  }, []);

  const restore = useCallback((id) => {
    // v1.0.4: restore explicitly un-minimizes (focus no longer does).
    setWindows(prev => {
      const z = ++topZ.current;
      return prev.map(w => (w.id === id ? { ...w, z, minimized: false } : w));
    });
  }, []);

  const setGeometry = useCallback((id, geom) => {
    setWindows(prev => prev.map(w => (w.id === id ? { ...w, ...geom } : w)));
  }, []);

  // Tile all non-minimized windows into a grid filling the workspace.
  // Reserves top space for the breadcrumb (90px, same as the Build 27
  // arrange fix) and bottom space for the pallet/taskbar.
  const tileAll = useCallback(() => {
    setWindows(prev => {
      const visible = prev.filter(w => !w.minimized);
      if (visible.length === 0) return prev;

      const TOP = 70;
      const BOTTOM = 90;
      const GAP = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const cols = Math.ceil(Math.sqrt(visible.length));
      const rows = Math.ceil(visible.length / cols);

      const availW = vw - GAP * (cols + 1);
      const availH = (vh - TOP - BOTTOM) - GAP * (rows + 1);
      const tileW = Math.floor(availW / cols);
      const tileH = Math.floor(availH / rows);

      let i = 0;
      const order = [...visible].sort((a, b) => a.z - b.z);
      const geomById = {};
      for (const w of order) {
        const r = Math.floor(i / cols);
        const c = i % cols;
        geomById[w.id] = {
          x: GAP + c * (tileW + GAP),
          y: TOP + GAP + r * (tileH + GAP),
          w: tileW,
          h: tileH
        };
        i++;
      }
      return prev.map(w =>
        geomById[w.id] ? { ...w, ...geomById[w.id], minimized: false } : w
      );
    });
  }, []);

  return {
    windows,
    open,
    close,
    closeAll,
    focus,
    minimize,
    restore,
    setGeometry,
    tileAll
  };
}
